<template>
  <v-flex xs12 class="artwork-public px-3 pb-5">
    <section class="public-section">
      <div class="public-section-header">
        <div>
          <div class="title font-weight-bold">Publication status</div>
          <div class="caption">Control whether the artwork appears in the public catalogue.</div>
        </div>
      </div>
      <visibility-settings :artwork="artwork" embedded />
    </section>

    <section class="public-section">
      <div class="public-section-header">
        <div>
          <div class="title font-weight-bold">Public website text</div>
          <div class="caption">Choose the artwork text displayed on its public detail page.</div>
        </div>
        <v-btn
          v-if="artwork.representative_text_id"
          small
          flat
          dark
          :loading="savingText"
          @click="selectPublicText(null)"
        >Clear selection</v-btn>
      </div>
      <v-progress-linear v-if="savingText" indeterminate height="2" class="ma-0" />
      <div v-if="artworkTexts.length === 0" class="public-empty">No artwork texts are available.</div>
      <v-radio-group
        v-else
        :value="artwork.representative_text_id"
        class="public-text-list ma-0 pa-0"
        hide-details
        @change="selectPublicText"
      >
        <v-card v-for="text in artworkTexts" :key="text.id" flat class="public-text-option">
          <v-radio :value="text.id" color="black">
            <div slot="label">
              <div class="subheading black--text">{{ text.title || 'Untitled text' }}</div>
              <div class="caption grey--text">{{ textSummary(text) }}</div>
            </div>
          </v-radio>
        </v-card>
      </v-radio-group>
    </section>

    <section class="public-section">
      <div class="public-section-header">
        <div>
          <div class="title font-weight-bold">Current public top image</div>
          <div class="caption">This image leads the artwork page on the public website.</div>
        </div>
      </div>
      <div v-if="publicTopPhoto" class="public-top-preview">
        <v-img v-if="photoUrl(publicTopPhoto) && !imageFailures[publicTopPhoto.id]" :src="photoUrl(publicTopPhoto)" contain height="420" class="grey lighten-3" @error="markImageFailed(publicTopPhoto.id)" />
        <div v-else class="public-image-placeholder"><v-icon>broken_image</v-icon><span>Image not ready</span></div>
        <artwork-media-status :media="publicTopPhoto" />
        <div class="public-top-caption mt-2">
          <div class="caption grey--text">{{ publicTopPhoto.fname || `Image ${publicTopPhoto.id}` }}</div>
          <v-btn
            small
            flat
            :loading="photoBusy === publicTopPhoto.id"
            :disabled="photoBusy !== null"
            @click="updatePhotoSelection(publicTopPhoto, true, false)"
          >Clear public top</v-btn>
        </div>
      </div>
      <div v-else class="public-empty">No public top image selected.</div>
    </section>

    <section class="public-section">
      <div class="public-section-header">
        <div>
          <div class="title font-weight-bold">Artwork photos</div>
          <div class="caption">Select public gallery images and choose one public top image.</div>
        </div>
        <div class="caption">{{ publicPhotoCount }} public / {{ artworkPhotos.length }} total</div>
      </div>
      <v-alert v-if="photoError" :value="true" type="error" outline class="ma-3">{{ photoError }}</v-alert>
      <div v-if="artworkPhotos.length === 0" class="public-empty">No imported artwork photos are available.</div>
      <v-container v-else grid-list-md fluid class="pa-3">
        <v-layout row wrap>
          <v-flex v-for="photo in artworkPhotos" :key="photo.id" xs12 sm6 md4 lg3>
            <v-card flat class="public-photo-card">
              <v-img v-if="photoUrl(photo) && !imageFailures[photo.id]" :src="photoUrl(photo)" contain height="180" class="grey lighten-3" @error="markImageFailed(photo.id)" />
              <div v-else class="public-image-placeholder public-image-placeholder--small"><v-icon small>broken_image</v-icon><span>Image not ready</span></div>
              <div class="public-photo-name">{{ photo.fname || `Image ${photo.id}` }}</div>
              <artwork-media-status :media="photo" />
              <v-btn v-if="isAdmin" small flat class="mx-2 mb-1" @click="editPhoto(photo)">
                <v-icon small left>edit</v-icon>Edit details
              </v-btn>
              <v-btn v-if="canRetryPhoto(photo)" small flat class="mx-2 mb-1" :loading="photoBusy === photo.id" @click="retryPhoto(photo)">
                <v-icon small left>refresh</v-icon>Retry failed versions
              </v-btn>
              <div class="public-photo-controls">
                <label>
                  <input
                    type="checkbox"
                    :checked="isPublicPhoto(photo)"
                    :disabled="photoBusy !== null || photoMediaActive(photo)"
                    @change="togglePublicPhoto(photo, $event)"
                  />
                  <span>Public website</span>
                </label>
                <label>
                  <input
                    type="radio"
                    :name="`public-top-photo-${artwork.id}`"
                    :checked="isPublicTop(photo)"
                    :disabled="photoBusy !== null || photoMediaActive(photo)"
                    @change="makePublicTop(photo)"
                  />
                  <span>Public top</span>
                </label>
                <v-progress-circular
                  v-if="photoBusy === photo.id"
                  indeterminate
                  :size="15"
                  :width="2"
                  color="grey darken-2"
                />
              </div>
            </v-card>
          </v-flex>
        </v-layout>
      </v-container>
    </section>

    <netx-images
      ref="netxImages"
      :artwork="artwork"
      context="public"
      @updated="refreshArtwork"
    />

    <artwork-photo-drawer
      v-model="photoDrawerOpen"
      :artwork="artwork"
      :photo-id="photoDrawerPhotoId"
      @saved="photoSaved"
    />

    <v-snackbar v-model="snackbar" right top :color="snackbarColor" :timeout="4000">
      {{ snackbarText }}
      <v-btn flat @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </v-flex>
</template>

<script>
import axios from '@/utils/apiClient'
import VisibilitySettings from '@/components/public/VisibilitySettings'
import NetxImages from './NetxImages'
import ArtworkMediaStatus from './ArtworkMediaStatus'
import ArtworkPhotoDrawer from './ArtworkPhotoDrawer'
import {
  canRetryMedia,
  cmsImageUrl,
  mediaActive,
  mediaStatus,
  normalizeMediaUrl,
  publicImageUrl,
  retryArtworkPhoto
} from '@/utils/artworkMedia'
import {
  loadNetxAssets,
  updateArtworkPhotoPublicSelection,
  updateNetxSelection
} from '@/utils/netxArtworkReview'

export default {
  name: 'ArtworkPublic',
  components: { NetxImages, VisibilitySettings, ArtworkMediaStatus, ArtworkPhotoDrawer },
  props: {
    artwork: { type: Object, required: true }
  },
  data: () => ({
    savingText: false,
    photoBusy: null,
    photoError: '',
    netxPollingTimer: null,
    mediaPollingTimer: null,
    snackbar: false,
    snackbarColor: 'black',
    snackbarText: '',
    imageFailures: {},
    photoDrawerOpen: false,
    photoDrawerPhotoId: null
  }),
  computed: {
    artworkTexts () {
      return Array.isArray(this.artwork.artwork_texts) ? this.artwork.artwork_texts : []
    },
    artworkPhotos () {
      const photos = Array.isArray(this.artwork.artwork_photos) ? this.artwork.artwork_photos.slice() : []
      return photos.sort((left, right) => {
        const publicDifference = Number(this.isPublicPhoto(right)) - Number(this.isPublicPhoto(left))
        return publicDifference || (left.position || 9999) - (right.position || 9999) || left.id - right.id
      })
    },
    publicTopPhoto () {
      return this.artworkPhotos.find(photo => this.isPublicTop(photo)) || null
    },
    publicPhotoCount () {
      return this.artworkPhotos.filter(photo => this.isPublicPhoto(photo)).length
    },
    canRetry () {
      return canRetryMedia(this.c_user)
    },
    isAdmin () {
      const roles = (this.c_user && this.c_user.roles) || []
      return !!(this.c_user && (this.c_user.admin === true || Number(this.c_user.admin) === 1)) ||
        roles.some(role => ['admin', 'superadmin', 'root'].includes(String(role.shortcut || role.role || '').toLowerCase()))
    }
  },
  methods: {
    editPhoto (photo) {
      this.photoDrawerPhotoId = photo.id
      this.photoDrawerOpen = true
    },
    async photoSaved (result) {
      this.notify(
        result.publicProcessingQueued
          ? 'Image details saved. Public image versions are processing.'
          : 'Image details saved.',
        'black'
      )
      if (result.publicProcessingQueued) this.scheduleMediaPolling(true)
      await this.refreshArtwork()
    },
    textSummary (text) {
      const value = (text.content || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
      return value ? value.slice(0, 180) : 'No text content'
    },
    photoUrl (photo) {
      return normalizeMediaUrl(cmsImageUrl(photo) || publicImageUrl(photo, 1280), this.$globals.apiserver)
    },
    markImageFailed (photoId) {
      if (photoId) this.$set(this.imageFailures, photoId, true)
    },
    canRetryPhoto (photo) {
      const status = mediaStatus(photo)
      return this.canRetry && ['failed', 'partial'].includes(status)
    },
    photoMediaActive (photo) {
      return mediaActive(photo)
    },
    async retryPhoto (photo) {
      if (!this.canRetryPhoto(photo) || this.photoBusy !== null) return
      this.photoBusy = photo.id
      this.photoError = ''
      try {
        await retryArtworkPhoto(photo.id)
        this.notify('Image processing queued.', 'black')
        await this.refreshArtwork()
      } catch (error) {
        this.photoError = (error.response && error.response.data && error.response.data.error) || 'Image processing could not be retried.'
      } finally {
        this.photoBusy = null
      }
    },
    isPublicPhoto (photo) {
      return Number(photo.public_site) === 1
    },
    isPublicTop (photo) {
      return Number(this.artwork.top_public_photo_id) === Number(photo.id)
    },
    async selectPublicText (textId) {
      if (this.savingText || textId === this.artwork.representative_text_id) return
      this.savingText = true
      try {
        await axios.patch(`/artworks/${this.artwork.id}/set_representative_text`, { text_id: textId })
        this.$set(this.artwork, 'representative_text_id', textId)
        this.notify('Public website text saved.', 'black')
        this.$emit('getArtwork')
      } catch (error) {
        this.notify('Could not save the public website text.', 'red')
      } finally {
        this.savingText = false
      }
    },
    togglePublicPhoto (photo, event) {
      const publicSite = event.target.checked
      this.updatePhotoSelection(photo, publicSite, publicSite && this.isPublicTop(photo))
    },
    makePublicTop (photo) {
      this.updatePhotoSelection(photo, true, true)
    },
    async updatePhotoSelection (photo, publicSite, publicTop) {
      if (this.photoBusy !== null) return
      const status = mediaStatus(photo)
      if (publicSite && !['ready', 'succeeded', 'complete', 'completed'].includes(status)) {
        this.photoError = 'This image cannot be published until all required versions are ready.'
        return
      }
      this.photoBusy = photo.id
      this.photoError = ''
      let queued = false

      try {
        if (photo.source_netx_asset_id) {
          await updateNetxSelection(this.artwork.id, photo.source_netx_asset_id, {
            imported: true,
            public_site: publicSite,
            cms_top: Number(this.artwork.top_photo_id) === Number(photo.id),
            public_top: publicTop
          })
          await this.waitForNetxSelection(photo.source_netx_asset_id)
        } else {
          const response = await updateArtworkPhotoPublicSelection(this.artwork.id, photo.id, {
            public_site: publicSite,
            public_top: publicTop
          })
          queued = !!(response.data && response.data.queued)
          if (queued) this.scheduleMediaPolling(true)
        }
        this.notify(queued ? 'Public image versions are processing.' : 'Public image selection saved.', 'black')
        await this.refreshArtwork()
      } catch (error) {
        this.photoError = (error.response && error.response.data && error.response.data.error) ||
          error.message || 'Public image selection could not be saved.'
      } finally {
        this.photoBusy = null
      }
    },
    waitForNetxSelection (assetId, attempts) {
      const attempt = attempts || 0
      return loadNetxAssets({ ids: [assetId] }).then(assets => {
        const asset = assets[0]
        if (!asset) throw new Error('The NetX image is no longer available.')
        if (asset.selection && asset.selection.active) {
          if (attempt >= 60) throw new Error('NetX image selection is taking too long.')
          return new Promise((resolve, reject) => {
            this.netxPollingTimer = window.setTimeout(() => {
              this.waitForNetxSelection(assetId, attempt + 1).then(resolve).catch(reject)
            }, 1200)
          })
        }
        if (asset.selection && asset.selection.status === 'failed') {
          throw new Error(asset.selection.error || 'NetX image selection failed.')
        }
        return asset
      })
    },
    async refreshArtwork () {
      this.$emit('getArtwork')
      if (this.$refs.netxImages) this.$refs.netxImages.loadAssets({ silent: true })
    },
    scheduleMediaPolling (force) {
      window.clearTimeout(this.mediaPollingTimer)
      const active = this.artworkPhotos.some(photo => mediaActive(photo))
      if (!force && !active) return

      this.mediaPollingTimer = window.setTimeout(() => {
        this.$emit('getArtwork')
        this.scheduleMediaPolling()
      }, 1500)
    },
    notify (text, color) {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    }
  },
  watch: {
    'artwork.artwork_photos': {
      deep: true,
      handler () {
        this.imageFailures = {}
        this.scheduleMediaPolling()
      }
    }
  },
  mounted () {
    this.scheduleMediaPolling()
  },
  beforeDestroy () {
    window.clearTimeout(this.netxPollingTimer)
    window.clearTimeout(this.mediaPollingTimer)
  }
}
</script>

<style scoped>
.artwork-public { width: 100%; }
.public-section { background: #fff; border: 1px solid #d8d8d8; margin-bottom: 20px; }
.public-section-header { align-items: center; background: #292929; color: #eee; display: flex; justify-content: space-between; min-height: 58px; padding: 10px 14px; }
.public-section-header .caption { color: #bbb; }
.public-empty { color: #666; font-size: 13px; padding: 22px 16px; }
.public-top-preview { padding: 16px; }
.public-top-caption { align-items: center; display: flex; justify-content: space-between; }
.public-text-list { padding: 12px !important; }
.public-text-option { border-bottom: 1px solid #e2e2dc; padding: 11px 13px; }
.public-text-option:last-child { border-bottom: 0; }
.public-photo-card { border: 1px solid #deded9; }
.public-image-placeholder { align-items: center; background: #ecece8; color: #777; display: flex; flex-direction: column; gap: 6px; height: 420px; justify-content: center; }
.public-image-placeholder--small { height: 180px; }
.public-image-placeholder span { font-size: 12px; }
.public-photo-name { font-size: 12px; font-weight: 700; min-height: 46px; overflow: hidden; padding: 9px 10px; }
.public-photo-controls { align-items: center; border-top: 1px solid #e2e2dc; display: flex; flex-wrap: wrap; gap: 8px 14px; min-height: 48px; padding: 8px 10px; }
.public-photo-controls label { align-items: center; cursor: pointer; display: flex; font-size: 12px; gap: 6px; }
.public-photo-controls input { height: 15px; margin: 0; width: 15px; }
</style>



// WEBPACK FOOTER //
// src/components/artworks/ArtworkPublic.vue